import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';

import { DateAdapter } from '@angular/material/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { User } from '../../../app/administration/users/user.model';
import { UsersService } from '../../../app/administration/users/users.service';
import { environment } from '../../../environments/environment';
import { mpsRouteNames } from '../../mps-route-names';
import { MpsNotificationService } from '../../services/mps-notification.service';
import { MpsUserService } from '../../services/mps-user.service';
import { MpsNavigationComponent } from '../mps-navigation/mps-navigation.component';

@Component({
  selector: 'mps-app-header',
  templateUrl: './mps-header.component.html',
  styleUrls: ['./mps-header.component.scss'],
  host: {
    '[class.header-hidden]': 'isHeaderHidden()',
    '[style.minHeight.px]': 'isMobile() ? headerHeight() : null',
  },
  imports: [
    TranslateModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    MpsNavigationComponent,
    CommonModule,
    RouterModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
})
export class MpsHeaderComponent implements OnInit {
  private static readonly MOBILE_BREAKPOINT = 992;
  private static readonly SCROLL_THRESHOLD = 10;

  @Input() public nav?: MatSidenav;
  @Input() public showNavigation = false;
  mpsUserService = inject(MpsUserService);
  _notificationService = inject(MpsNotificationService);
  translateService = inject(TranslateService);
  private _dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);
  private _eRef = inject(ElementRef);
  private _destroyRef = inject(DestroyRef);
  private _scrollContainer: HTMLElement | null = null;
  user = signal(this.mpsUserService.getUserSubject().getValue());
  isDropdownOpen = signal(false);
  logoutRoute = mpsRouteNames.auth + '/' + mpsRouteNames.logout;
  readonly canImpersonate = environment.allowImpersonation;
  private _usersService = inject(UsersService);
  search = new FormControl();
  usersToImpersonate = signal<User[]>([]);

  isHeaderHidden = signal(false);
  isMobile = signal(false);
  headerHeight = signal(0);

  private _lastScrollY = 0;
  private _ticking = false;

  constructor() {
    afterNextRender(() => this._initScrollBehavior());
  }

  private _initScrollBehavior(): void {
    this._updateMobileState();
    this._measureHeaderHeight();
    this._scrollContainer = this._findScrollableAncestor();
    this._attachScrollListener();
    this._attachResizeListener();
    this._attachSidenavListener();
  }

  private _attachScrollListener(): void {
    if (!this._scrollContainer) return;

    const onScroll = () => {
      if (!this._ticking) {
        requestAnimationFrame(() => {
          this._onScroll();
          this._ticking = false;
        });
        this._ticking = true;
      }
    };
    this._scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    this._destroyRef.onDestroy(() => this._scrollContainer?.removeEventListener('scroll', onScroll));
  }

  private _attachResizeListener(): void {
    const onResize = () => {
      this._updateMobileState();
      this._measureHeaderHeight();
    };
    window.addEventListener('resize', onResize, { passive: true });
    this._destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
  }

  private _attachSidenavListener(): void {
    if (!this.nav) return;
    const sub = this.nav.openedChange.subscribe((opened) => {
      if (opened) {
        this.isHeaderHidden.set(false);
      }
    });
    this._destroyRef.onDestroy(() => sub.unsubscribe());
  }

  ngOnInit(): void {
    this.search.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((searchInput) => searchInput.length >= 3)
      )
      .subscribe((searchInput) => {
        this._usersService.usersToImpersonate(searchInput).subscribe({
          next: (users) => {
            this.usersToImpersonate.set(users);
          },
          error: () => {
            this.usersToImpersonate.set([]);
            this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
          },
        });
      });
  }

  getCurrentLangLabel(): string {
    return this.getLangLabel(this.translateService.currentLang);
  }

  getLangLabel(lang: string): string {
    const labels: Record<string, string> = {
      fr: 'Français',
      en: 'English',
    };
    return labels[lang] || this.translateService.instant('general.missing_translation');
  }

  toggleDropdown() {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  switchLanguage(language: string) {
    this.translateService.use(language);
    this._dateAdapter.setLocale(language);
    const user = this.mpsUserService.getUser();
    localStorage.setItem('lang', language);
    if (user) {
      this.mpsUserService.changeLang(user.id, language)?.subscribe();
    }
  }

  getUserDisplayName() {
    return this.user()?.firstname + ' ' + this.user()?.lastname.substring(0, 1) + '.';
  }

  getUserInitials(): string {
    const userData = this.user();
    if (!userData) return '';
    const firstInitial = userData.firstname?.charAt(0)?.toUpperCase() || '';
    const lastInitial = userData.lastname?.charAt(0)?.toUpperCase() || '';
    return firstInitial + lastInitial;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (!this._eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
    }
  }

  displayUser(user?: User | null): string {
    return user ? `${user.igg} - ${user.firstname} ${user.lastname} | ${user.role.label}` : '';
  }

  impersonate(user: User): void {
    this._usersService.impersonate(user.id).subscribe({
      next: (token: string) => {
        localStorage.setItem('token', token);
        window.location.reload();
      },
      error: () => {
        this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
      },
    });
  }

  private _onScroll(): void {
    if (!this.isMobile() || !this._scrollContainer) return;

    const currentScrollY = this._scrollContainer.scrollTop;

    // Toujours visible en haut de page (gère aussi le rubber-band iOS)
    if (currentScrollY <= 0) {
      this.isHeaderHidden.set(false);
      this._lastScrollY = currentScrollY;
      return;
    }

    // Ne pas cacher si le sidenav est ouvert
    if (this.nav?.opened) {
      this._lastScrollY = currentScrollY;
      return;
    }

    const delta = currentScrollY - this._lastScrollY;

    if (Math.abs(delta) >= MpsHeaderComponent.SCROLL_THRESHOLD) {
      this.isHeaderHidden.set(delta > 0);
      this._lastScrollY = currentScrollY;
    }
  }

  private _findScrollableAncestor(): HTMLElement | null {
    // Capturé une seule fois au rendu initial — suppose que la structure DOM ne change pas après l'init
    let el = this._eRef.nativeElement.parentElement;
    while (el) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  private _updateMobileState(): void {
    const wasMobile = this.isMobile();
    this.isMobile.set(window.innerWidth < MpsHeaderComponent.MOBILE_BREAKPOINT);

    // Reset au passage mobile → desktop
    if (wasMobile && !this.isMobile()) {
      this.isHeaderHidden.set(false);
    }
  }

  private _measureHeaderHeight(): void {
    const headerEl = this._eRef.nativeElement.querySelector('header');
    this.headerHeight.set(headerEl?.offsetHeight ?? 64);
  }
}
